import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Link } from 'react-router';
import { UserCheck, Loader2, ArrowRight } from 'lucide-react';

interface DashboardStats {
    event: {
        id: number;
        name: string;
    } | null;
    stats: {
        totalParticipations: number;
        arrivedParticipants: number;
    };
}

export const CheckInWidget = () => {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3100/api';

    const { data, isLoading } = useQuery<DashboardStats>({
        queryKey: ['retreat-dashboard-stats'],
        queryFn: async () => {
            const res = await axios.get(`${apiUrl}/retreat/dashboard/stats`, { withCredentials: true });
            return res.data;
        },
        refetchInterval: 30000, // Refresh every 30s during check-in
    });

    if (isLoading) {
        return (
            <div className="h-full flex items-center justify-center text-gray-400">
                <Loader2 className="animate-spin text-primary" />
            </div>
        );
    }

    if (!data?.event) {
        return null; // Don't show if no event
    }

    const percent = data.stats.totalParticipations > 0
        ? Math.round((data.stats.arrivedParticipants / data.stats.totalParticipations) * 100)
        : 0;

    return (
        <div className="h-full flex flex-col justify-between space-y-4">
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <div className="flex flex-col">
                        <h4 className="font-bold text-charcoal truncate max-w-[200px]" title={data.event.name}>
                            Anreise-Status
                        </h4>
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{data.event.name}</span>
                    </div>
                    <div className="text-sm font-black text-primary bg-primary/5 px-2 py-1 rounded-lg border border-primary/10">
                        {percent}%
                    </div>
                </div>

                <div className="bg-slate-50/50 rounded-2xl p-4 border border-slate-100 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-primary text-white flex items-center justify-center shadow-lg shadow-primary/20">
                        <UserCheck size={24} />
                    </div>
                    <div className="flex-1">
                        <div className="text-2xl font-black text-charcoal">
                            {data.stats.arrivedParticipants} <span className="text-gray-300 font-bold text-lg">/ {data.stats.totalParticipations}</span>
                        </div>
                        <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mt-0.5">Personen vor Ort</div>
                    </div>
                </div>

                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                        className="h-full bg-primary transition-all duration-500 ease-out"
                        style={{ width: `${percent}%` }}
                    />
                </div>
            </div>

            <Link
                to="/retreat/checkin"
                className="flex items-center justify-between px-4 py-3 bg-charcoal text-white rounded-xl text-sm font-bold hover:bg-charcoal/90 transition-all group shadow-lg shadow-charcoal/10"
            >
                Check-In öffnen
                <ArrowRight size={16} className="text-white/50 group-hover:text-white transition-all group-hover:translate-x-1" />
            </Link>
        </div>
    );
};
